// ============================================================================
// useChat — Chat state management hook with SSE streaming
// ============================================================================

import { useEffect, useState, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api/client'
import {
  streamChat,
  extractRenderPayload,
  extractWidgetCode,
  useConversationMessages,
  useInvalidateConversations,
  type SSEEvent,
  type ChatMessageData,
  type ChatAttachment,
} from '@/services/api/chat'
import type { RenderPayload } from '@/types/chatRenders'

// ============================================================================
// TYPES
// ============================================================================

export interface WidgetPreview {
  widgetId: string
  title: string
  code: string
  dataKeys?: string[]
  action: 'created' | 'updated'
  /** True when this preview was derived from a `chart` render payload —
   *  MessageBubble then shows it via InsightChartCard, not WidgetPreviewCard. */
  fromRender?: boolean
}

/** Lightweight attachment metadata for rendering on a user message bubble.
 *  The raw base64 payload is not retained in state — only what's needed to
 *  show a chip (filename + media type). */
export interface ChatAttachmentMeta {
  filename: string
  media_type: string
}

/**
 * The bubble text a voice-only turn shows until the backend's transcript comes
 * back on the next conversation load. It is DISPLAY ONLY: it is not the user's
 * words, and sending it back as if it were is how a conversation ended up with
 * a stored user turn that literally read "🎤 Voice message" (2026-10-21).
 */
export const VOICE_PLACEHOLDER = '🎤 Voice message'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  isStreaming?: boolean
  /** Set on a user turn that carried audio whose payload is not retained in
   *  state, so the turn cannot be re-sent faithfully: `content` is then either
   *  the display placeholder (voice-only) or only the typed half of a mixed
   *  typed+voice turn. Regenerate refuses these — see `regenerableText`. */
  audioNotRetained?: boolean
  toolCalls?: ToolCall[]
  widgets?: WidgetPreview[]
  attachments?: ChatAttachmentMeta[]
  /** Rich card payloads carried on tool results, in stream order. */
  renders?: RenderPayload[]
  /** Suggested follow-up prompts from the `complete` event. */
  followups?: string[]
}

export interface ToolCall {
  tool: string
  args: Record<string, unknown>
  result?: unknown
  status: 'calling' | 'complete'
  injected?: boolean
  /** True when this call's result carried a render payload — the pill then
   *  yields to the card (staff keep the pill for the raw debug view). */
  hasRender?: boolean
}

/**
 * Build a WidgetPreview from a `chart` render so the existing
 * WidgetPreviewCard/WidgetSandbox path can mount the widget inline.
 * Returns null for non-chart renders or when no code rides on the result
 * (InsightChartCard then fetches the code by widget id instead).
 */
export function widgetPreviewFromRender(
  render: RenderPayload,
  result: unknown
): WidgetPreview | null {
  if (render.type !== 'chart') return null
  const widgetId = render.spec.widget_id
  if (widgetId == null || (typeof widgetId !== 'number' && typeof widgetId !== 'string')) {
    return null
  }
  const code = extractWidgetCode(result)
  if (!code) return null
  const title = typeof render.spec.title === 'string' ? render.spec.title : render.entity.title
  return {
    widgetId: String(widgetId),
    title,
    code,
    action: render.status === 'created' ? 'created' : 'updated',
    fromRender: true,
  }
}

/** Index of the newest user turn, or -1. */
function lastUserIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return i
  }
  return -1
}

/**
 * The text regenerating this user turn would re-send, or null when the turn
 * must not be re-sent at all.
 *
 * Null covers three cases, all of which would otherwise put words in the user's
 * mouth: a turn that carried audio (the payload is gone, so only a fragment or
 * a placeholder is left), a turn with no text, and a turn whose content IS the
 * display placeholder — matched by string too, because a conversation hydrated
 * from the backend can hold the placeholder as real content (turns stored
 * before this guard existed).
 */
export function regenerableText(message: ChatMessage | undefined): string | null {
  if (!message || message.role !== 'user' || message.audioNotRetained) return null
  if (!message.content.trim() || message.content.trim() === VOICE_PLACEHOLDER) return null
  return message.content
}

/** Dashboard views/widgets tools whose use should refetch the current view
 *  grid (the unified Flow chat replaces the old Builder agent). */
const VIEW_MUTATION_TOOLS = new Set([
  'create_widget', 'update_widget', 'delete_widget',
  'add_widget_to_view', 'remove_widget_from_view', 'rearrange_view',
  'create_view', 'update_view', 'delete_view',
])

/** Tools that change a training plan, so a host showing that plan has to
 *  refetch. There is no server-side marker for "Flow edited the plan", so this
 *  is the client's own list and it drifts if the backend renames a tool. Names
 *  are the canonical ones in beoflow `ai_agent/shared_context/tool_names.py`;
 *  the read tools beside them (get_program, get_schedule, get_periodization)
 *  are deliberately absent. */
const PLAN_MUTATION_TOOLS = new Set([
  'update_program',
  'upsert_sessions', 'update_session', 'delete_session',
  'update_schedule',
  'update_periodization',
])

export interface UseChatOptions {
  /**
   * Conversation to open on mount, from `?conversation=<id>`. Seeded rather
   * than loaded in an effect so a deep link cannot fight a later selection.
   */
  initialConversationId?: number | null
  /** The dashboard the chat is launched from. Passed to the backend so the
   *  views/widgets tools target it; also drives onViewMutated refetches. */
  viewId?: number
  /** Called once at end-of-turn when any view/widget mutation tool ran, so the
   *  host (e.g. ViewDetailPage) can invalidate and refetch the view grid. */
  onViewMutated?: () => void
  /** Scope every turn to one training plan, so Flow's program and schedule
   *  tools target it. The stream REJECTS a plan the coach does not own or that
   *  does not exist, so the host must surface that rather than retry. */
  planId?: number | null
  /** Scope every turn to one team (the team overview's selected group). */
  groupUuid?: string | null
  /** Called once at end-of-turn when any plan mutation tool ran, so the host
   *  can refetch the plan's events and show the edit landing. */
  onPlanMutated?: () => void
}

// ============================================================================
// HOOK
// ============================================================================

export function useChat(targetProfileId?: number | null, opts?: UseChatOptions) {
  const viewId = opts?.viewId
  const onViewMutated = opts?.onViewMutated
  const planId = opts?.planId
  const groupUuid = opts?.groupUuid
  const onPlanMutated = opts?.onPlanMutated
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<number | null>(opts?.initialConversationId ?? null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  // The conversation whose next hydration should be skipped, because our local
  // messages are already the truth for it (we just streamed the turn).
  //
  // Carries the id rather than being a bare boolean, and that is load-bearing:
  // as a boolean armed on `complete` it could be consumed by a DIFFERENT
  // conversation's hydration. Finish a turn in 903 while 903's messages are
  // still in flight, click through to 77, and 77's hydration ate the flag and
  // rendered an empty thread. Cross-model review caught it with a failing test.
  const skipHydrationForRef = useRef<number | null>(null)
  const loadedConversationIdRef = useRef<number | null>(null)
  // The id from this turn's `start` frame, held until the turn ends. A ref, not
  // state, on purpose: setting conversationId mid-stream fires
  // useConversationMessages and races hydrateMessages against the bubble still
  // streaming. Promoted to state on every terminal path instead — see
  // adoptPendingConversation.
  const pendingConversationIdRef = useRef<number | null>(null)
  const invalidateConversations = useInvalidateConversations(targetProfileId)

  /**
   * Name the conversation this turn ran in, whatever ended it.
   *
   * The backend saves the turn on the error, superseded and user-stop paths
   * too, so without this the thread is on the server and the next message
   * still forks into a new one. Arms the hydration guard the same way the
   * `complete` path does, so adopting the id does not wipe the messages
   * already on screen.
   */
  const adoptPendingConversation = useCallback(() => {
    const pending = pendingConversationIdRef.current
    pendingConversationIdRef.current = null
    if (pending == null) return
    skipHydrationForRef.current = pending
    loadedConversationIdRef.current = pending
    setConversationId(prev => (prev === pending ? prev : pending))
  }, [])

  // Switching athlete must drop the previous athlete's conversation — but the
  // effect below also runs on mount, where it would null out a conversation id
  // seeded from `?conversation=<id>` before its messages ever hydrate. Reset on
  // a real change of athlete only.
  const lastProfileIdRef = useRef(targetProfileId)
  // The athlete this chat's state is settled for. Lags targetProfileId by one
  // render on a switch (until the reset below commits), so a caller can tell
  // "bound to the new athlete" from "still holding the old conversation".
  const [settledProfileId, setSettledProfileId] = useState(targetProfileId)

  useEffect(() => {
    if (lastProfileIdRef.current === targetProfileId) return
    lastProfileIdRef.current = targetProfileId
    abortControllerRef.current?.abort()
    pendingConversationIdRef.current = null
    loadedConversationIdRef.current = null
    skipHydrationForRef.current = null
    setConversationId(null)
    setMessages([])
    setIsStreaming(false)
    setError(null)
    setSettledProfileId(targetProfileId)
  }, [targetProfileId])

  // Load messages for a conversation from the backend
  const messagesQuery = useConversationMessages(conversationId, targetProfileId)

  /**
   * Load a conversation — set the ID and populate messages from backend data.
   */
  const loadConversation = useCallback((id: number | null) => {
    if (id === conversationId) return

    // Stop any in-flight stream
    abortControllerRef.current?.abort()
    pendingConversationIdRef.current = null
    // A stale skip armed for a different thread would otherwise eat this one's
    // first hydration if the athlete navigates back to it.
    skipHydrationForRef.current = null
    setIsStreaming(false)
    setError(null)
    loadedConversationIdRef.current = id
    setConversationId(id)
    setMessages([])
  }, [conversationId])

  /**
   * Populate messages state from backend query data.
   * Called by the component when messagesQuery.data changes.
   */
  const hydrateMessages = useCallback((data: ChatMessageData[]) => {
    if (skipHydrationForRef.current !== null && skipHydrationForRef.current === conversationId) {
      skipHydrationForRef.current = null
      return
    }

    const hydrated: ChatMessage[] = []
    let currentAssistant: ChatMessage | null = null

    for (const msg of data) {
      if (msg.role === 'user') {
        // Flush any pending assistant message
        if (currentAssistant) {
          hydrated.push(currentAssistant)
          currentAssistant = null
        }
        hydrated.push({
          id: `msg-${msg.created_at}`,
          role: 'user',
          content: msg.content,
          timestamp: msg.created_at,
        })
      } else if (msg.role === 'assistant') {
        if (msg.message_type === 'tool_calls' && msg.tool_calls?.length) {
          if (!currentAssistant) {
            currentAssistant = {
              id: `msg-${msg.created_at}`,
              role: 'assistant',
              content: '',
              timestamp: msg.created_at,
              toolCalls: [],
            }
          }
          currentAssistant.toolCalls = currentAssistant.toolCalls ?? []
          msg.tool_calls.forEach(toolCall => {
            currentAssistant!.toolCalls!.push({
              tool: toolCall.tool ?? 'unknown',
              args: toolCall.args ?? {},
              status: 'complete',
            })
          })
        } else if (msg.message_type === 'tool_results' && msg.tool_results?.length) {
          if (currentAssistant?.toolCalls?.length) {
            msg.tool_results.forEach(toolResult => {
              const matchingTool = [...currentAssistant!.toolCalls!].reverse().find((tc: ToolCall) => tc.tool === toolResult.tool)
              if (matchingTool) {
                matchingTool.result = toolResult.result
              }
              // Reconstruct render payloads persisted inside the result dicts.
              const render = extractRenderPayload(toolResult.result)
              if (render) {
                if (matchingTool) matchingTool.hasRender = true
                currentAssistant!.renders = currentAssistant!.renders ?? []
                currentAssistant!.renders.push({ ...render, tool: toolResult.tool })
                const preview = widgetPreviewFromRender(render, toolResult.result)
                if (preview) {
                  currentAssistant!.widgets = currentAssistant!.widgets ?? []
                  currentAssistant!.widgets.push(preview)
                }
              }
            })
          }
        } else if (msg.message_type === 'tool_call') {
          // Legacy singular shape
          if (!currentAssistant) {
            currentAssistant = {
              id: `msg-${msg.created_at}`,
              role: 'assistant',
              content: '',
              timestamp: msg.created_at,
              toolCalls: [],
            }
          }
          currentAssistant.toolCalls = currentAssistant.toolCalls ?? []
          currentAssistant.toolCalls.push({
            tool: msg.tool_name ?? 'unknown',
            args: msg.tool_args ?? {},
            status: 'complete',
          })
        } else if (msg.message_type === 'tool_result') {
          // Legacy singular shape
          if (currentAssistant?.toolCalls?.length) {
            const lastTool = currentAssistant.toolCalls[currentAssistant.toolCalls.length - 1]
            if (lastTool.tool === msg.tool_name) {
              lastTool.result = msg.tool_result
              const render = extractRenderPayload(msg.tool_result)
              if (render) {
                lastTool.hasRender = true
                currentAssistant.renders = currentAssistant.renders ?? []
                currentAssistant.renders.push({ ...render, tool: lastTool.tool })
                const preview = widgetPreviewFromRender(render, msg.tool_result)
                if (preview) {
                  currentAssistant.widgets = currentAssistant.widgets ?? []
                  currentAssistant.widgets.push(preview)
                }
              }
            }
          }
        } else {
          // Text message
          if (currentAssistant) {
            // This text follows tool calls — attach to existing assistant message
            currentAssistant.content = msg.content
          } else {
            currentAssistant = {
              id: `msg-${msg.created_at}`,
              role: 'assistant',
              content: msg.content,
              timestamp: msg.created_at,
            }
          }
        }
      }
    }

    // Flush final assistant message
    if (currentAssistant) {
      hydrated.push(currentAssistant)
    }

    loadedConversationIdRef.current = conversationId
    setMessages(hydrated)
  }, [conversationId])

  /**
   * Send a message and stream the response.
   */
  const sendMessage = useCallback(async (text: string, attachments?: ChatAttachment[], audio?: string) => {
    const hasAttachments = !!attachments && attachments.length > 0
    if ((!text.trim() && !hasAttachments && !audio) || isStreaming) return

    setError(null)

    // Add user message. For a voice-only turn the text is empty until the
    // backend returns the transcript on the next conversation reload — show a
    // placeholder so the bubble isn't blank (mirrors iOS).
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text || (audio ? VOICE_PLACEHOLDER : ''),
      timestamp: new Date().toISOString(),
      ...(audio ? { audioNotRetained: true } : {}),
      ...(hasAttachments
        ? { attachments: attachments!.map(a => ({ filename: a.filename, media_type: a.media_type })) }
        : {}),
    }

    // Add placeholder assistant message for streaming
    const assistantId = `assistant-${Date.now()}`
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      toolCalls: [],
    }

    setMessages(prev => [...prev, userMsg, assistantMsg])
    setIsStreaming(true)

    const controller = new AbortController()
    abortControllerRef.current = controller

    // Track whether any dashboard view/widget mutation ran this turn, so we can
    // refetch the view grid once when the turn completes.
    let viewMutatedThisTurn = false
    let planMutatedThisTurn = false

    const handleEvent = (event: SSEEvent) => {
      switch (event.type) {
        case 'start':
          // Held, not applied — see pendingConversationIdRef.
          pendingConversationIdRef.current = event.data.conversation_id
          break

        case 'token':
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: m.content + event.data.content }
                : m
            )
          )
          break

        case 'tool_call':
          if (VIEW_MUTATION_TOOLS.has(event.data.tool)) viewMutatedThisTurn = true
          if (PLAN_MUTATION_TOOLS.has(event.data.tool)) planMutatedThisTurn = true
          setMessages(prev => {
            const updated = prev.map(m => {
              if (m.id !== assistantId) return m
              const toolCalls = [...(m.toolCalls ?? [])]
              toolCalls.push({
                tool: event.data.tool,
                args: event.data.args,
                status: 'calling',
                injected: event.data.injected ?? false,
              })
              return { ...m, toolCalls }
            })
            return updated
          })
          break

        case 'tool_result': {
          const render = extractRenderPayload(event.data.result)
          const preview = render ? widgetPreviewFromRender(render, event.data.result) : null
          setMessages(prev =>
            prev.map(m => {
              if (m.id !== assistantId) return m
              const toolCalls = (m.toolCalls ?? []).map(tc =>
                tc.tool === event.data.tool && tc.status === 'calling'
                  ? {
                      ...tc,
                      result: event.data.result,
                      status: 'complete' as const,
                      ...(render ? { hasRender: true } : {}),
                    }
                  : tc
              )
              return {
                ...m,
                toolCalls,
                ...(render
                  ? { renders: [...(m.renders ?? []), { ...render, tool: event.data.tool }] }
                  : {}),
                ...(preview ? { widgets: [...(m.widgets ?? []), preview] } : {}),
              }
            })
          )
          break
        }

        case 'complete': {
          const newConvId = event.data.conversation_id
          pendingConversationIdRef.current = null
          skipHydrationForRef.current = newConvId
          loadedConversationIdRef.current = newConvId
          setConversationId(newConvId)

          setMessages(prev => {
            const updated = prev.map(m => {
              if (m.id !== assistantId) return m
              const completeMessage = event.data.message?.trim()
              const updatedContent = completeMessage && !m.content.includes(completeMessage)
                ? m.content + event.data.message
                : m.content
              const followups = (event.data.followups ?? []).filter(
                (f): f is string => typeof f === 'string' && f.trim().length > 0
              )
              return {
                ...m,
                content: updatedContent,
                isStreaming: false,
                ...(followups.length ? { followups } : {}),
              }
            })
            return updated
          })

          setIsStreaming(false)
          invalidateConversations()
          // Refetch the dashboard grid if any widget/view tool mutated it.
          if (viewMutatedThisTurn) onViewMutated?.()
          // Same at end-of-turn for the plan, so the calendar shows what moved.
          if (planMutatedThisTurn) onPlanMutated?.()
          break
        }

        case 'error':
          setError(event.data.error)
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, isStreaming: false, content: m.content || event.data.partial || '' }
                : m
            )
          )
          setIsStreaming(false)
          // AGENT_STALLED, STREAM_TIMEOUT, STREAM_SUPERSEDED and backend
          // exceptions all save the turn server-side. Keep the thread.
          adoptPendingConversation()
          break
      }
    }

    try {
      await streamChat(text, conversationId, targetProfileId, handleEvent, controller.signal, attachments, viewId, audio, planId, groupUuid)
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message)
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantId ? { ...m, isStreaming: false } : m
          )
        )
        setIsStreaming(false)
      }
      // Includes AbortError: stopStreaming tells the backend to save the turn,
      // so a stopped turn has a conversation to go back to just like a failed
      // one. Runs before the throw is swallowed either way.
      adoptPendingConversation()
    }
  }, [conversationId, isStreaming, invalidateConversations, targetProfileId, viewId, onViewMutated, planId, groupUuid, onPlanMutated, adoptPendingConversation])

  /**
   * Regenerate the last assistant turn: re-send the last user message through
   * the normal stream path, replacing the assistant turn (and re-appending the
   * user message via sendMessage). Attachments are not re-sent — their raw
   * payloads are not retained in state, and neither is recorded audio: a turn
   * that carried audio is not regenerable at all rather than being re-sent as
   * a substitute (see `regenerableText`).
   */
  const regenerateLast = useCallback(() => {
    if (isStreaming) return
    const lastUserIdx = lastUserIndex(messages)
    if (lastUserIdx < 0) return
    const text = regenerableText(messages[lastUserIdx])
    if (text == null) return
    setMessages(prev => prev.slice(0, lastUserIdx))
    void sendMessage(text)
  }, [messages, isStreaming, sendMessage])

  // Drives whether hosts offer the Regenerate control at all: a dead control is
  // worse than none, and the alternative is re-sending a message the user never
  // typed.
  const canRegenerateLast = regenerableText(messages[lastUserIndex(messages)]) != null

  /**
   * Stop the current stream.
   */
  const stopStreaming = useCallback(() => {
    // Tell the backend to stop the agent turn (not just close the socket):
    // without this signal the backend treats the aborted connection as an
    // accidental disconnect and runs the turn to completion, saving the
    // message and pushing a "finished" notice anyway. Fire-and-forget — the
    // apiClient carries the session baggage that keys the in-flight stream.
    void apiClient
      .post('/ai/interactive/cancel/')
      .catch(() => { /* best-effort; the abort below still disconnects */ })
    abortControllerRef.current?.abort()
    setIsStreaming(false)
    setMessages(prev =>
      prev.map(m => (m.isStreaming ? { ...m, isStreaming: false } : m))
    )
  }, [])

  /**
   * Start a new conversation.
   */
  const newConversation = useCallback(() => {
    abortControllerRef.current?.abort()
    // The abort lands in sendMessage's catch, which would otherwise adopt the
    // turn we are walking away from and undo this. Same in loadConversation
    // and the athlete switch: an explicit choice of thread beats the id of the
    // turn being abandoned.
    pendingConversationIdRef.current = null
    skipHydrationForRef.current = null
    loadedConversationIdRef.current = null
    setConversationId(null)
    setMessages([])
    setIsStreaming(false)
    setError(null)
  }, [])

  /**
   * Dismiss the error banner.
   */
  const dismissError = useCallback(() => setError(null), [])

  return {
    messages,
    conversationId,
    isStreaming,
    error,
    messagesQuery,
    sendMessage,
    regenerateLast,
    canRegenerateLast,
    stopStreaming,
    loadConversation,
    hydrateMessages,
    newConversation,
    dismissError,
    settledProfileId,
  }
}
